/**
 * Canvas 尺寸规范化配置选项
 */
export interface NormalizeCanvasSizeOptions {
  /** 最小值，默认为 0 */
  min?: number;
  /** 最大值，默认为 4294967295 (32位无符号整数的最大值) */
  max?: number;
  /** 当值为 0 时的默认值，默认为 100 */
  defaultValue?: number;
  /** 是否允许值为 0，如果为 false 且值为 0，则使用 defaultValue，默认为 false */
  allowZero?: boolean;
}

/**
 * 规范化 Canvas 尺寸
 * 将输入值转换为合法的 Canvas 尺寸值
 *
 * @param size - 原始尺寸值（可以是数字、字符串或其他类型）
 * @param options - 配置选项
 * @returns 规范化后的尺寸值
 *
 * @example
 * ```typescript
 * // 基本用法
 * normalizeCanvasSize(123.7) // => 123
 * normalizeCanvasSize('456.9') // => 456
 * normalizeCanvasSize(0) // => 100 (默认值)
 *
 * // 自定义配置
 * normalizeCanvasSize(0, { defaultValue: 200 }) // => 200
 * normalizeCanvasSize(0, { allowZero: true }) // => 0
 * normalizeCanvasSize(5000000000, { max: 1000 }) // => 1000
 * ```
 */
export function normalizeCanvasSize(
  size: number | string | undefined | null,
  options: NormalizeCanvasSizeOptions = {}
): number {
  const {
    min = 0,
    max = 4294967295, // 32位无符号整数的最大值
    defaultValue = 100,
    allowZero = false
  } = options;

  // 1. 转换为数字，非数字则默认0
  const validSize = Number(size) || 0;

  // 2. 取整（去除小数）
  const intSize = Math.floor(validSize);

  // 3. 限制在合法范围
  const finalSize = Math.max(min, Math.min(intSize, max));

  // 4. 兜底：如果最终尺寸为0且不允许为0，设为默认值
  if (finalSize === 0 && !allowZero) {
    return defaultValue;
  }

  return finalSize;
}

/**
 * 规范化 Canvas 尺寸（宽度和高度）
 * 批量处理宽度和高度，使用相同的配置
 *
 * @param width - 原始宽度值
 * @param height - 原始高度值
 * @param options - 配置选项
 * @returns 规范化后的宽度和高度对象
 *
 * @example
 * ```typescript
 * // 基本用法
 * normalizeCanvasDimensions(123.7, 456.9) // => { width: 123, height: 456 }
 * normalizeCanvasDimensions(0, 0) // => { width: 100, height: 100 }
 *
 * // 自定义配置
 * normalizeCanvasDimensions(0, 0, { defaultValue: 200 }) // => { width: 200, height: 200 }
 * ```
 */
export function normalizeCanvasDimensions(
  width: number | string | undefined | null,
  height: number | string | undefined | null,
  options: NormalizeCanvasSizeOptions = {}
): { width: number; height: number } {
  return {
    width: normalizeCanvasSize(width, options),
    height: normalizeCanvasSize(height, options)
  };
}
